/**
 * @relatedFiles ../app/layout.tsx, ./theme-provider.tsx, ./theme-color-meta.tsx
 * @purpose Provides Clerk authentication provider with dynamic theme support and error handling for auth failures
 * @notes Intercepts fetch requests to show user-friendly error messages for Clerk API failures
 */
'use client';

import { ClerkProvider } from '@clerk/nextjs';
import { dark } from '@clerk/themes';
import { useTheme } from 'next-themes';
import { toast } from 'sonner';

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

interface ClerkThemeProviderProps {
  children: ReactNode;
}

export function ClerkThemeProvider({ children }: ClerkThemeProviderProps) {
  const { resolvedTheme } = useTheme();
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  useEffect(() => {
    setIsDarkMode(resolvedTheme === 'dark');
  }, [resolvedTheme]);
  
  useEffect(() => {
    const originalFetch = window.fetch;
    
    window.fetch = async (input, init) => {
      const response = await originalFetch(input, init);
      
      const urlString = typeof input === 'string' 
        ? input 
        : input instanceof Request 
          ? input.url 
          : input.toString();
      
      if (urlString.includes('clerk') && response.status >= 500) {
        toast.error('Authentication service is temporarily unavailable. Please try again later.');
      }
      
      return response;
    };
    
    return () => {
      window.fetch = originalFetch;
    };
  }, []);
  
  return (
    <ClerkProvider
      appearance={{
        baseTheme: isDarkMode ? dark : undefined,
        variables: {
          colorPrimary: isDarkMode ? '#61afef' : '#383a42',
          colorBackground: isDarkMode ? '#2D2F34' : '#ffffff',
          colorText: isDarkMode ? '#e6e6e6' : '#1a1a1a',
          colorTextSecondary: isDarkMode ? '#c0c0c0' : '#4b4b4b',
          colorInputBackground: isDarkMode ? '#363940' : '#ffffff',
          colorInputText: isDarkMode ? '#e6e6e6' : '#1a1a1a',
          colorSuccess: isDarkMode ? '#98c379' : '#50a14f',
          colorDanger: isDarkMode ? '#e06c75' : '#e45649',
          colorWarning: isDarkMode ? '#d19a66' : '#c18401',
        }
      }}
    >
      {children}
    </ClerkProvider>
  );
} 